import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { api } from '../api/client';
import { AdminToggles } from '../types';
import { useAuth } from './AuthContext';

type RCPage = 'linking' | 'link-qualification' | 'maturity-scoring' | 'weight-scoring' | 'maturity-qualification' | 'vrio';

/** Maps each RC Assessment page to the toggle that gates it */
const PAGE_TOGGLE_MAP: Record<RCPage, keyof AdminToggles> = {
  'linking': 'rcLinking',
  'link-qualification': 'rcLinkQualification',
  'maturity-scoring': 'rcScoring',
  'weight-scoring': 'rcScoring',
  'maturity-qualification': 'rcWeightMaturityQualification',
  'vrio': 'rcVrio',
};

interface TogglesContextValue {
  toggles: AdminToggles;
  loading: boolean;
  setToggle: (key: keyof AdminToggles, enabled: boolean) => Promise<void>;
  refreshToggles: () => Promise<void>;
  /** Check if a user can access an RC Assessment tab (admins always true) */
  isTabAccessible: (page: RCPage) => boolean;
  /** Raw toggle check — is this tab unlocked for regular users? (no admin bypass) */
  isTabUnlockedForUsers: (page: RCPage) => boolean;
  /** Check if the aggregate view in Linking RC is available (requires rcLinkQualification) */
  isLinkingAggregateEnabled: () => boolean;
}

const defaultToggles: AdminToggles = {
  rcLinking: false,
  rcLinkQualification: false,
  rcScoring: false,
  rcWeightMaturityQualification: false,
  rcVrio: false,
};

const TogglesContext = createContext<TogglesContextValue | null>(null);

export function TogglesProvider({ children }: { children: ReactNode }) {
  const [toggles, setToggles] = useState<AdminToggles>(defaultToggles);
  const [loading, setLoading] = useState(true);
  const { user, isAdmin } = useAuth();

  const refreshToggles = useCallback(async () => {
    if (!user) return;
    try {
      const data = await api.getToggles() as AdminToggles;
      setToggles(data);
    } catch (err) {
      console.error('Failed to fetch toggles:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      refreshToggles();
      const interval = setInterval(refreshToggles, 30000);
      return () => clearInterval(interval);
    }
  }, [user, refreshToggles]);

  const setToggle = async (key: keyof AdminToggles, enabled: boolean) => {
    await api.setToggle(key, enabled);
    setToggles((prev) => ({ ...prev, [key]: enabled }));
  };

  const isTabAccessible = useCallback((page: RCPage): boolean => {
    if (isAdmin) return true;
    return toggles[PAGE_TOGGLE_MAP[page]];
  }, [isAdmin, toggles]);

  const isTabUnlockedForUsers = useCallback((page: RCPage): boolean => {
    return toggles[PAGE_TOGGLE_MAP[page]];
  }, [toggles]);

  const isLinkingAggregateEnabled = useCallback((): boolean => {
    if (isAdmin) return true;
    return toggles.rcLinkQualification;
  }, [isAdmin, toggles.rcLinkQualification]);

  return (
    <TogglesContext.Provider value={{ toggles, loading, setToggle, refreshToggles, isTabAccessible, isTabUnlockedForUsers, isLinkingAggregateEnabled }}>
      {children}
    </TogglesContext.Provider>
  );
}

export function useToggles() {
  const context = useContext(TogglesContext);
  if (!context) {
    throw new Error('useToggles must be used within TogglesProvider');
  }
  return context;
}
